import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  exitVideoFullscreen,
  isLandscapeVideo,
  prepareLandscapeHotPlayback,
} from '@/utils/fullscreen';
import Hls from 'hls.js';

import {
  createMemberPaymentTicket,
  createMemberRechargeOrder,
  fetchDramaDetail,
  fetchHomeDisplayConfig,
  fetchMemberPaymentOptions,
  isInsufficientPointsError,
  queryMemberRechargeOrder,
  recordDramaView,
  recordDramaWatch,
  unlockDramaEpisode,
  type DramaDetail,
} from '@/api/member';
import { mapDramaCard, type CinemaEpisode, type CinemaSeries } from '@/utils/cinemaData';
import {
  ensureMemberAccessToken,
  readMemberSession,
  refreshCurrentMember,
  type MemberSession,
} from '@/utils/memberSession';
import CinemaRechargeModal from '../components/RechargeModal';
import { isNativeAppRuntime } from '@/utils/nativeApp';
import { shouldUseMobileExperience } from '@/utils/device';
import { loadSubtitleCues, selectInterfaceSubtitle, subtitleTextAt } from '@/utils/subtitlePlayback';

interface QualityChoice {
  id: string;
  label: string;
  level?: number;
  url?: string;
}

export default function CinemaWatchPage() {
  const { t, i18n } = useTranslation('cinema');
  const navigate = useNavigate();
  const { seriesId } = useParams<{ seriesId: string }>();

  const [detail, setDetail] = useState<DramaDetail | null>(null);
  const [series, setSeries] = useState<CinemaSeries | null>(null);
  const [playingEpisode, setPlayingEpisode] = useState<CinemaEpisode | null>(null);
  const [unlockTarget, setUnlockTarget] = useState<CinemaEpisode | null>(null);
  const [nextEpisodeTarget, setNextEpisodeTarget] = useState<CinemaEpisode | null>(null);
  const [member, setMember] = useState<MemberSession>(() => readMemberSession());
  const [showInsufficient, setShowInsufficient] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  useEffect(() => { setUnlockError(''); }, [unlockTarget]);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activePlayerSelector, setActivePlayerSelector] = useState<'quality' | 'speed' | 'subtitle' | null>(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [qualityMode, setQualityMode] = useState('auto');
  const [qualityChoices, setQualityChoices] = useState<QualityChoice[]>([]);
  const [subtitleLanguage, setSubtitleLanguage] = useState(() => localStorage.getItem('cinema:subtitle-language') || 'auto');
  const [subtitleText, setSubtitleText] = useState('');
  const [appDownloadUrl, setAppDownloadUrl] = useState('');
  const [appLoginRewardPoints, setAppLoginRewardPoints] = useState(100);
  const [showAppDownload, setShowAppDownload] = useState(false);
  const [mobileExperience, setMobileExperience] = useState(shouldUseMobileExperience);
  const recordedWatchRef = useRef('');
  const recordedViewRef = useRef('');
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const playbackRateRef = useRef(1);
  const isNativeApp = isNativeAppRuntime();

  const subtitleTracks = playingEpisode?.subtitleTracks || [];
  const activeSubtitle = useMemo(() => {
    if (subtitleLanguage === 'off') return null;
    const automaticTrack = selectInterfaceSubtitle(subtitleTracks, i18n.resolvedLanguage || i18n.language);
    if (subtitleLanguage !== 'auto') {
      const preferredLanguage = subtitleLanguage.toLowerCase();
      return subtitleTracks.find((track) => track.languageCode.toLowerCase() === preferredLanguage)
        || subtitleTracks.find((track) => track.languageCode.toLowerCase().split('-')[0] === preferredLanguage.split('-')[0])
        || automaticTrack;
    }
    return automaticTrack;
  }, [i18n.language, i18n.resolvedLanguage, subtitleLanguage, subtitleTracks]);

  useEffect(() => {
    const video = videoRef.current;
    setSubtitleText('');
    if (!video || !activeSubtitle) return undefined;

    let cancelled = false;
    let cues: Awaited<ReturnType<typeof loadSubtitleCues>> = [];
    const updateSubtitle = () => {
      if (!cancelled) setSubtitleText(subtitleTextAt(cues, video.currentTime, mobileExperience ? 34 : 72));
    };
    const loadActiveSubtitle = () => {
      void loadSubtitleCues(activeSubtitle)
        .then((loaded) => {
          if (cancelled) return;
          cues = loaded;
          updateSubtitle();
        })
        .catch(() => undefined);
    };
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) loadActiveSubtitle();
    else video.addEventListener('canplay', loadActiveSubtitle, { once: true });
    video.addEventListener('timeupdate', updateSubtitle);
    video.addEventListener('seeking', updateSubtitle);
    return () => {
      cancelled = true;
      video.removeEventListener('canplay', loadActiveSubtitle);
      video.removeEventListener('timeupdate', updateSubtitle);
      video.removeEventListener('seeking', updateSubtitle);
    };
  }, [activeSubtitle?.id, activeSubtitle?.url, activeSubtitle?.version, mobileExperience, playingEpisode?.id]);

  const selectSubtitle = (language: string) => {
    setSubtitleLanguage(language);
    localStorage.setItem('cinema:subtitle-language', language);
    setActivePlayerSelector(null);
  };

  useEffect(() => {
    const syncDevice = () => setMobileExperience(shouldUseMobileExperience());
    syncDevice();
    window.addEventListener('resize', syncDevice, { passive: true });
    window.addEventListener('orientationchange', syncDevice, { passive: true });
    return () => {
      window.removeEventListener('resize', syncDevice);
      window.removeEventListener('orientationchange', syncDevice);
    };
  }, []);

  const unlockedEpisodes = useMemo(
    () => new Set((series?.episodes || []).filter((episode) => !episode.locked).map((episode) => episode.id)),
    [series?.episodes],
  );

  useEffect(() => {
    let cancelled = false;
    const dramaId = Number(seriesId);
    if (!dramaId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void ensureMemberAccessToken()
      .then((token) => fetchDramaDetail(dramaId, token || undefined))
      .then((row) => {
        if (cancelled) return;
        const mapped = mapDramaCard(row);
        const active =
          mapped.episodes.find((episode) => String(row.currentEpisode?.id || '') === episode.id)
          || mapped.episodes.find((episode) => !episode.locked)
          || mapped.episodes[0]
          || null;
        setDetail(row);
        setSeries(mapped);
        setPlayingEpisode(active);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [i18n.resolvedLanguage, seriesId]);

  useEffect(() => {
    if (isNativeApp) {
      setAppDownloadUrl('');
      return;
    }
    let cancelled = false;
    void fetchHomeDisplayConfig()
      .then((config) => {
        if (!cancelled) {
          setAppDownloadUrl((config.appDownloadUrl || '').trim());
          setAppLoginRewardPoints(Math.max(0, Number(config.appLoginRewardPoints) || 0));
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isNativeApp]);

  useEffect(() => {
    if (isNativeApp) {
      setShowAppDownload(false);
      return;
    }
    if (!detail?.id || !playingEpisode?.id) {
      setShowAppDownload(false);
      return;
    }

    const storageKey = `cinema:app-download:${detail.id}:${playingEpisode.id}`;
    try {
      if (window.sessionStorage.getItem(storageKey)) {
        setShowAppDownload(false);
        return;
      }
      window.sessionStorage.setItem(storageKey, '1');
    } catch {
      // Private browsing may block storage; the banner can still be closed normally.
    }
    setShowAppDownload(true);
  }, [detail?.id, isNativeApp, playingEpisode?.id]);

  const syncDetail = (row: DramaDetail, preferredEpisodeId?: string) => {
    const mapped = mapDramaCard(row);
    const active =
      mapped.episodes.find((episode) => episode.id === preferredEpisodeId)
      || mapped.episodes.find((episode) => String(row.currentEpisode?.id || '') === episode.id)
      || mapped.episodes.find((episode) => !episode.locked)
      || mapped.episodes[0]
      || null;
    setDetail(row);
    setSeries(mapped);
    setPlayingEpisode(active);
  };

  const handleEpisodeClick = (ep: CinemaEpisode) => {
    setNextEpisodeTarget(null);
    if (ep.locked && !unlockedEpisodes.has(ep.id)) {
      setUnlockTarget(ep);
      setShowInsufficient(false);
      return;
    }
    setPlayingEpisode(ep);
    void ensureMemberAccessToken().then((token) => {
      if (!token || !detail || ep.episodeNum <= 1) return;
      void recordDramaWatch(detail.id, Number(ep.id), token).catch(() => undefined);
    });
  };

  useEffect(() => {
    if (!detail || !playingEpisode || playingEpisode.episodeNum <= 1) return;

    const watchKey = `${detail.id}:${playingEpisode.id}`;
    if (recordedWatchRef.current === watchKey) return;
    recordedWatchRef.current = watchKey;

    void ensureMemberAccessToken().then((token) => {
      if (!token) return;
      void recordDramaWatch(detail.id, Number(playingEpisode.id), token).catch(() => {
        recordedWatchRef.current = '';
      });
    });
  }, [detail?.id, playingEpisode?.episodeNum, playingEpisode?.id]);

  const handleVideoEnded = () => {
    exitVideoFullscreen(videoRef.current);
    if (!series || !playingEpisode) return;
    const currentIndex = series.episodes.findIndex((episode) => episode.id === playingEpisode.id);
    const nextEpisode = series.episodes[currentIndex + 1];
    if (!nextEpisode) return;
    if (nextEpisode.locked && !unlockedEpisodes.has(nextEpisode.id)) {
      setUnlockTarget(nextEpisode);
      setShowInsufficient(false);
      return;
    }
    setNextEpisodeTarget(nextEpisode);
  };

  const handleUnlockConfirm = () => {
    if (!unlockTarget || !detail) return;
    void ensureMemberAccessToken().then(async (token) => {
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        const result = await unlockDramaEpisode(detail.id, token);
        syncDetail(result.drama, unlockTarget.id);
        setMember(await refreshCurrentMember());
        setUnlockTarget(null);
        setShowInsufficient(false);
        const nextEpisode = result.drama.episodes.find((episode) => String(episode.id) === unlockTarget.id);
        if (nextEpisode && nextEpisode.episodeNo > 1) {
          void recordDramaWatch(result.drama.id, nextEpisode.id, token).catch(() => undefined);
        }
      } catch (error) {
        setShowInsufficient(isInsufficientPointsError(error));
        setUnlockError(isInsufficientPointsError(error) ? '' : '解锁失败，请稍后重试');
        if (isInsufficientPointsError(error)) {
          window.setTimeout(() => setRechargeOpen(true), 1000);
        }
      }
    });
  };

  const currentApiEpisode = detail?.episodes.find((episode) => String(episode.id) === playingEpisode?.id);
  const currentVideoUrl = currentApiEpisode?.videoUrl || '';
  const isHlsVideo = /\.m3u8(?:$|[?#])/i.test(currentVideoUrl);

  useEffect(() => {
    setQualityMode('auto');
    setQualityChoices([]);

    const video = videoRef.current;
    if (!video || !currentVideoUrl) return;

    hlsRef.current?.destroy();
    hlsRef.current = null;

    const playVideo = async () => {
      video.playbackRate = playbackRateRef.current;
      try {
        await video.play();
      } catch {
        if (video.muted) return;

        // Browsers commonly reject autoplay with sound. Retry muted so playback still starts.
        video.muted = true;
        await video.play().catch(() => undefined);
      }
    };

    if (isHlsVideo && Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        startLevel: -1,
      });
      hlsRef.current = hls;
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(currentVideoUrl));
      hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
        const levels = data.levels.map((level, index) => ({
          id: `level-${index}`,
          label: level.name?.trim() || (level.height ? `${level.height}P` : `线路${index + 1}`),
          level: index,
        }));
        setQualityChoices([{ id: 'auto', label: 'auto', level: -1 }, ...levels]);
        void playVideo();
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          hls.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        }
      });

      return () => {
        hls.destroy();
        if (hlsRef.current === hls) hlsRef.current = null;
      };
    }

    video.src = currentVideoUrl;
    video.load();
    video.addEventListener('loadedmetadata', playVideo, { once: true });

    if (!isHlsVideo) {
      const label = currentApiEpisode?.quality?.trim() || 'source';
      setQualityMode('source');
      setQualityChoices([{ id: 'source', label }]);
      return () => video.removeEventListener('loadedmetadata', playVideo);
    }

    const controller = new AbortController();
    void fetch(currentVideoUrl, { signal: controller.signal })
      .then((response) => response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`)))
      .then((manifest) => {
        const lines = manifest.split(/\r?\n/);
        const choices: QualityChoice[] = [{ id: 'auto', label: 'auto', url: currentVideoUrl }];
        for (let index = 0; index < lines.length; index += 1) {
          const info = lines[index].trim();
          if (!info.startsWith('#EXT-X-STREAM-INF:')) continue;
          const path = lines[index + 1]?.trim();
          if (!path || path.startsWith('#')) continue;
          const name = info.match(/(?:^|,)NAME="?([^",]+)"?/i)?.[1];
          const height = info.match(/RESOLUTION=\d+x(\d+)/i)?.[1];
          choices.push({
            id: `variant-${choices.length}`,
            label: name?.trim() || (height ? `${height}P` : `线路${choices.length}`),
            url: new URL(path, currentVideoUrl).toString(),
          });
        }
        setQualityChoices(choices);
      })
      .catch(() => setQualityChoices([{ id: 'auto', label: 'auto', url: currentVideoUrl }]));

    return () => {
      controller.abort();
      video.removeEventListener('loadedmetadata', playVideo);
    };
  }, [currentApiEpisode?.quality, currentVideoUrl, isHlsVideo]);

  if (loading || !series || !playingEpisode) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center pt-20">
        <div className="text-center px-6">
          <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-foreground-50/5">
            <i className="ri-loader-4-line text-3xl text-foreground-500 animate-spin"></i>
          </span>
          <p className="text-foreground-500 text-sm">{loading ? '加载中...' : t('cinema_noSeries')}</p>
        </div>
      </div>
    );
  }

  const playbackRates = [0.5, 0.75, 1, 1.25, 1.5, 2];
  const currentQuality = qualityChoices.find((choice) => choice.id === qualityMode) || qualityChoices[0];

  const selectQuality = (choice: QualityChoice) => {
    const video = videoRef.current;
    const hls = hlsRef.current;
    if (hls && typeof choice.level === 'number') {
      hls.currentLevel = choice.level;
    } else if (video && choice.url && video.currentSrc !== choice.url) {
      const currentTime = video.currentTime;
      const wasPaused = video.paused;
      video.src = choice.url;
      video.load();
      video.addEventListener('loadedmetadata', () => {
        video.currentTime = Math.min(currentTime, Number.isFinite(video.duration) ? video.duration : currentTime);
        video.playbackRate = playbackRateRef.current;
        if (!wasPaused) void video.play().catch(() => undefined);
      }, { once: true });
    }
    setQualityMode(choice.id);
    setActivePlayerSelector(null);
  };

  const selectPlaybackRate = (rate: number) => {
    playbackRateRef.current = rate;
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
    setPlaybackRate(rate);
  };

  const openHotPlayback = async () => {
    const video = videoRef.current;
    const landscapePlayback = Boolean(
      video
      && (mobileExperience || isNativeApp)
      && isLandscapeVideo(video),
    );
    const params = new URLSearchParams({
      video: String(detail.id),
      episode: playingEpisode.id,
      time: (video?.currentTime || 0).toFixed(3),
      rate: String(playbackRateRef.current),
      muted: video?.muted ? '1' : '0',
      landscape: landscapePlayback ? '1' : '0',
    });
    if (landscapePlayback) {
      await prepareLandscapeHotPlayback();
    }
    navigate(`/mylist?${params.toString()}`);
  };

  const handleMobileFullscreen = () => {
    void openHotPlayback();
  };

  return (
    <div className="pt-14 md:pt-16">
      <div className="max-w-7xl mx-auto px-2.5 sm:px-4 md:px-10 lg:px-14 py-3 md:py-6 space-y-4 md:space-y-6">
        <div className="flex min-w-0 items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-sm text-foreground-500 hover:text-foreground-300 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </span>
            <span className="hidden sm:inline">{t('cinema_backToList')}</span>
          </button>
          <span className="text-foreground-700 hidden sm:inline">/</span>
          <h1 className="text-base md:text-base font-semibold text-foreground-100 font-heading truncate">{series.title}</h1>
          <div className={`${mobileExperience ? 'hidden' : 'block'} relative shrink-0`}>
            <button
              type="button"
              onClick={() => setActivePlayerSelector((value) => value === 'quality' ? null : 'quality')}
              className="rounded-full bg-background-900/60 px-2.5 py-1 text-xs text-foreground-300 transition-colors hover:bg-background-800"
              aria-label={t('cinema_playerQuality')}
              aria-expanded={activePlayerSelector === 'quality'}
            >
              {currentQuality?.id === 'auto' ? t('cinema_playerAuto') : currentQuality?.label === 'source' ? t('cinema_playerSource') : currentQuality?.label || t('cinema_playerAuto')}
            </button>
            {activePlayerSelector === 'quality' && (
              <div className="absolute left-0 top-8 z-30 flex min-w-28 flex-wrap gap-1.5 rounded-lg border border-background-700 bg-background-900 p-2 shadow-xl">
                {qualityChoices.map((choice) => (
                  <button
                    key={choice.id}
                    type="button"
                    onClick={() => selectQuality(choice)}
                    className={`rounded px-2 py-1 text-xs whitespace-nowrap transition-colors ${
                      qualityMode === choice.id ? 'bg-accent-500 text-background-950' : 'text-foreground-300 hover:bg-background-800'
                    }`}
                  >
                    {choice.id === 'auto' ? t('cinema_playerAuto') : choice.label === 'source' ? t('cinema_playerSource') : choice.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className={`${mobileExperience ? 'hidden' : 'block'} relative shrink-0`}>
            <button
              type="button"
              onClick={() => setActivePlayerSelector((value) => value === 'speed' ? null : 'speed')}
              className="rounded-full bg-background-900/60 px-2.5 py-1 text-xs text-foreground-300 transition-colors hover:bg-background-800"
              aria-label={t('cinema_playerSpeed')}
              aria-expanded={activePlayerSelector === 'speed'}
            >
              {playbackRate}x
            </button>
            {activePlayerSelector === 'speed' && (
              <div className="absolute left-0 top-8 z-30 flex w-44 flex-wrap gap-1.5 rounded-lg border border-background-700 bg-background-900 p-2 shadow-xl">
                {playbackRates.map((rate) => (
                  <button
                    key={rate}
                    type="button"
                    onClick={() => {
                      selectPlaybackRate(rate);
                      setActivePlayerSelector(null);
                    }}
                    className={`rounded px-2 py-1 text-xs transition-colors ${
                      playbackRate === rate ? 'bg-accent-500 text-background-950' : 'text-foreground-300 hover:bg-background-800'
                    }`}
                  >
                    {rate}x
                  </button>
                ))}
              </div>
            )}
          </div>
          {subtitleTracks.length > 0 && (
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={() => setActivePlayerSelector((value) => value === 'subtitle' ? null : 'subtitle')}
                className="rounded-full bg-background-900/60 px-2.5 py-1 text-xs text-foreground-300 transition-colors hover:bg-background-800"
                aria-label={t('cinema_playerSubtitles')}
                aria-expanded={activePlayerSelector === 'subtitle'}
              >
                <i className="ri-closed-captioning-line mr-1" />
                {activeSubtitle?.languageName || (subtitleLanguage === 'off' ? t('cinema_subtitleOff') : t('cinema_subtitleAuto'))}
              </button>
              {activePlayerSelector === 'subtitle' && (
                <div className="absolute right-0 top-8 z-30 flex min-w-36 flex-col gap-1 rounded-lg border border-background-700 bg-background-900 p-2 shadow-xl">
                  <button type="button" onClick={() => selectSubtitle('auto')} className={`rounded px-2 py-1 text-left text-xs ${subtitleLanguage === 'auto' ? 'bg-accent-500 text-background-950' : 'text-foreground-300 hover:bg-background-800'}`}>{t('cinema_subtitleAuto')}</button>
                  <button type="button" onClick={() => selectSubtitle('off')} className={`rounded px-2 py-1 text-left text-xs ${subtitleLanguage === 'off' ? 'bg-accent-500 text-background-950' : 'text-foreground-300 hover:bg-background-800'}`}>{t('cinema_subtitleOff')}</button>
                  {subtitleTracks.map((track) => (
                    <button key={track.id} type="button" onClick={() => selectSubtitle(track.languageCode)} className={`rounded px-2 py-1 text-left text-xs whitespace-nowrap ${subtitleLanguage === track.languageCode ? 'bg-accent-500 text-background-950' : 'text-foreground-300 hover:bg-background-800'}`}>{track.languageName || track.languageCode}</button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div
          ref={playerContainerRef}
          className={`${mobileExperience ? 'h-[min(58svh,560px)] min-h-[390px] rounded-lg' : 'h-[clamp(240px,calc(100svh-8rem),760px)] min-h-0 rounded-2xl'} relative flex flex-col overflow-hidden bg-black fullscreen:h-[100dvh] fullscreen:min-h-0 fullscreen:rounded-none`}
        >
          <video
            ref={videoRef}
            src={isHlsVideo ? undefined : currentVideoUrl}
            poster={playingEpisode.thumbnailUrl}
            className="min-h-0 w-full flex-1 object-contain bg-black"
            controls
            autoPlay
            playsInline
            onPlay={() => {
              const viewKey = `${detail.id}:${playingEpisode.id}`;
              if (recordedViewRef.current === viewKey) return;
              recordedViewRef.current = viewKey;
              void recordDramaView(detail.id).catch(() => {
                if (recordedViewRef.current === viewKey) recordedViewRef.current = '';
              });
            }}
            onLoadedMetadata={() => {
              if (videoRef.current) videoRef.current.playbackRate = playbackRate;
            }}
            onEnded={handleVideoEnded}
          />
          <div
            className="pointer-events-none flex h-12 shrink-0 items-center justify-center bg-black px-4 md:h-16 md:px-8"
            aria-live="polite"
          >
            {subtitleText && (
              <p className="max-w-full -translate-y-5 whitespace-nowrap rounded-md bg-black/65 px-3 py-1 text-center text-[15px] font-medium leading-relaxed text-white shadow-xl md:max-w-[88%] md:px-4 md:py-1.5 md:text-lg">
                {subtitleText}
              </p>
            )}
          </div>
        </div>

        <div className={`flex ${mobileExperience ? 'flex-col gap-4' : 'flex-row gap-6'}`}>
          <div className="flex-1 min-w-0 space-y-3 md:space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm md:text-xs text-foreground-500 bg-background-900/60 px-2.5 md:px-2 py-1.5 md:py-1 rounded-full whitespace-nowrap">
                第{playingEpisode.episodeNum}集
              </span>
              <span className="text-sm md:text-xs text-accent-400 bg-accent-400/10 px-2.5 md:px-2 py-1.5 md:py-1 rounded-full whitespace-nowrap">
                {t('cinema_playing')}
              </span>
              <div className={`${mobileExperience ? 'block' : 'hidden'} relative`}>
                <button
                  type="button"
                  onClick={() => setActivePlayerSelector((value) => value === 'quality' ? null : 'quality')}
                  className="text-sm md:text-xs text-foreground-300 bg-background-900/60 px-2.5 md:px-2 py-1.5 md:py-1 rounded-full whitespace-nowrap hover:bg-background-800 transition-colors"
                  aria-label={t('cinema_playerQuality')}
                  aria-expanded={activePlayerSelector === 'quality'}
                >
                  {currentQuality?.id === 'auto' ? t('cinema_playerAuto') : currentQuality?.label === 'source' ? t('cinema_playerSource') : currentQuality?.label || t('cinema_playerAuto')}
                </button>
                {activePlayerSelector === 'quality' && (
                  <div className="absolute bottom-[calc(100%+0.5rem)] left-0 z-30 flex min-w-28 origin-bottom-left flex-wrap gap-1.5 rounded-lg border border-background-700 bg-background-900 p-2 shadow-xl">
                    {qualityChoices.map((choice) => (
                      <button
                        key={choice.id}
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          selectQuality(choice);
                        }}
                        className={`rounded px-2 py-1 text-xs whitespace-nowrap transition-colors ${
                          qualityMode === choice.id ? 'bg-accent-500 text-background-950' : 'text-foreground-300 hover:bg-background-800'
                        }`}
                      >
                        {choice.id === 'auto' ? t('cinema_playerAuto') : choice.label === 'source' ? t('cinema_playerSource') : choice.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className={`${mobileExperience ? 'block' : 'hidden'} relative`}>
                <button
                  type="button"
                  onClick={() => setActivePlayerSelector((value) => value === 'speed' ? null : 'speed')}
                  className="text-sm md:text-xs text-foreground-300 bg-background-900/60 px-2.5 md:px-2 py-1.5 md:py-1 rounded-full whitespace-nowrap hover:bg-background-800 transition-colors"
                  aria-label={t('cinema_playerSpeed')}
                  aria-expanded={activePlayerSelector === 'speed'}
                >
                  {playbackRate}x
                </button>
                {activePlayerSelector === 'speed' && (
                  <div className="absolute bottom-[calc(100%+0.5rem)] right-0 z-30 flex w-44 origin-bottom-right flex-wrap gap-1.5 rounded-lg border border-background-700 bg-background-900 p-2 shadow-xl">
                    {playbackRates.map((rate) => (
                      <button
                        key={rate}
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          selectPlaybackRate(rate);
                          setActivePlayerSelector(null);
                        }}
                        className={`rounded px-2 py-1 text-xs transition-colors ${
                          playbackRate === rate ? 'bg-accent-500 text-background-950' : 'text-foreground-300 hover:bg-background-800'
                        }`}
                      >
                        {rate}x
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleMobileFullscreen}
                className={`${mobileExperience ? 'flex' : 'hidden'} h-8 items-center justify-center gap-1 rounded-full bg-background-900/60 px-2.5 text-foreground-200 transition-colors hover:bg-background-800`}
                aria-label="全屏播放"
                title="全屏播放"
              >
                <i className="ri-fullscreen-line text-base" />
                <span className="text-xs font-medium whitespace-nowrap">全屏</span>
              </button>
              <span className="text-xs text-foreground-600 ml-auto">{playingEpisode.duration}</span>
            </div>

            <h2 className="text-xl md:text-xl font-bold text-foreground-50 font-heading">{playingEpisode.title}</h2>
            <div className="flex items-center gap-2 md:gap-3 text-sm md:text-xs text-foreground-500 flex-wrap">
              <span>{series.category}</span>
              <span className="w-0.5 h-0.5 rounded-full bg-foreground-700" />
              <span>{series.totalEpisodes} 集</span>
              <span className="w-0.5 h-0.5 rounded-full bg-foreground-700" />
              <span>{t('cinema_balance', { coins: member.pointsBalance })}</span>
            </div>
          </div>

          <div className={`${mobileExperience ? 'w-full' : 'w-72'} flex-shrink-0`}>
              <h4 className="text-sm md:text-xs font-semibold text-foreground-400 mb-2.5 md:mb-3 uppercase tracking-wide">
              {t('cinema_episodeList')} ({series.totalEpisodes})
            </h4>
            <div className="space-y-1.5 max-h-[300px] md:max-h-[400px] overflow-y-auto scrollbar-hide">
              {series.episodes.map((ep) => {
                const isActive = ep.id === playingEpisode.id;
                const isUnlocked = !ep.locked || unlockedEpisodes.has(ep.id);
                const normalizedTitle = ep.title.replace(/\s/g, '');
                const isGenericTitle = !normalizedTitle || normalizedTitle === `第${ep.episodeNum}集`;
                const secondaryText = isGenericTitle
                  ? isActive
                    ? t('cinema_playing')
                    : isUnlocked
                      ? t('cinema_unlocked')
                      : t('cinema_locked')
                  : ep.title;
                const hasDuration = Boolean(ep.duration && ep.duration !== '--:--');
                return (
                  <button
                    key={ep.id}
                    onClick={() => handleEpisodeClick(ep)}
                    className={`flex h-16 w-full cursor-pointer items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-colors ${
                      isActive
                        ? 'border-[#F19744]/60 bg-[#F19744]/[0.08]'
                        : 'border-transparent bg-background-900/40 hover:border-background-700 hover:bg-background-900/80'
                    }`}
                  >
                    <div className="relative h-12 w-20 flex-shrink-0 overflow-hidden rounded-md">
                      <img src={ep.thumbnailUrl} alt={ep.title} className="w-full h-full object-cover" />
                      {!isUnlocked && (
                        <div className="absolute inset-0 bg-background-950/60 flex items-center justify-center">
                          <span className="w-5 h-5 flex items-center justify-center text-foreground-600">
                            <i className="ri-lock-line text-xs"></i>
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <span className={`shrink-0 text-xs font-semibold ${isActive ? 'text-[#F19744]' : 'text-foreground-300'}`}>
                          第{ep.episodeNum}集
                        </span>
                        {!isUnlocked && (
                          <span className="text-[10px] text-accent-500 flex items-center gap-0.5">
                            <i className="ri-coin-line text-[9px]"></i>
                            {ep.cost}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex min-w-0 items-center gap-2 text-[10px]">
                        <span className={`min-w-0 flex-1 truncate ${isActive ? 'text-[#F2B06B]' : 'text-foreground-500'}`}>{secondaryText}</span>
                        {isUnlocked && hasDuration && <span className="shrink-0 text-foreground-600">{ep.duration}</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-background-900/40 rounded-xl p-4 md:p-5">
          <div className="flex items-center gap-2 mb-2 md:mb-3">
            <span className="w-5 h-5 flex items-center justify-center text-accent-500">
              <i className="ri-information-line"></i>
            </span>
            <h3 className="text-sm font-semibold text-foreground-300">{t('cinema_seriesDesc')}</h3>
          </div>
          <p className="text-xs md:text-sm text-foreground-400 leading-relaxed">{series.description}</p>
        </div>
      </div>

      {showAppDownload && (
        <div className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-40 px-2 sm:px-4 md:bottom-4">
          <div className="mx-auto flex w-full max-w-7xl items-center gap-3 rounded-xl border border-background-700 bg-background-950/95 px-3 py-2.5 shadow-2xl backdrop-blur-xl sm:px-5 sm:py-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-foreground-50 sm:text-base">
                {t('cinema_appDownloadTitle')}
              </p>
              <p className={`mt-0.5 truncate text-[11px] sm:text-xs ${appLoginRewardPoints > 0 ? 'font-semibold text-accent-400' : 'text-foreground-500'}`}>
                {appLoginRewardPoints > 0
                  ? t('cinema_appDownloadReward', { points: appLoginRewardPoints })
                  : t('cinema_appDownloadDesc')}
              </p>
            </div>
            {appDownloadUrl ? (
              <a
                href={appDownloadUrl}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 rounded-full bg-accent-500 px-4 py-2 text-xs font-bold text-background-950 transition-colors hover:bg-accent-500/90 sm:px-5 sm:text-sm"
              >
                {t('cinema_appDownloadButton')}
              </a>
            ) : (
              <button
                type="button"
                disabled
                title={t('cinema_appDownloadUnavailable')}
                className="shrink-0 cursor-not-allowed rounded-full bg-accent-500/50 px-4 py-2 text-xs font-bold text-background-950/70 sm:px-5 sm:text-sm"
              >
                {t('cinema_appDownloadButton')}
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowAppDownload(false)}
              title={t('cinema_cancel')}
              aria-label={t('cinema_cancel')}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-background-700 bg-background-900 text-foreground-500 transition-colors hover:text-foreground-100 sm:h-9 sm:w-9"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>
      )}

      {nextEpisodeTarget && !unlockTarget && (
        <div className="cinema-modal-safe fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setNextEpisodeTarget(null)} />
          <div className="relative w-full rounded-t-2xl border border-background-800 bg-background-900 p-5 shadow-2xl animate-ao-fade-in-up sm:max-w-sm sm:rounded-2xl md:p-6">
            <button
              type="button"
              onClick={() => setNextEpisodeTarget(null)}
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-background-800 text-foreground-500 transition-colors hover:text-foreground-300"
              aria-label={t('cinema_cancel')}
            >
              <i className="ri-close-line" />
            </button>

            <div className="text-center">
              <span className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-accent-500/15 text-accent-500 md:mb-4 md:h-16 md:w-16">
                <i className="ri-skip-forward-line text-xl md:text-2xl" />
              </span>
              <h3 className="font-heading text-base font-bold text-foreground-50 md:text-lg">{t('cinema_episodeEnded')}</h3>
              <p className="mt-1 text-xs text-foreground-500 md:mt-2 md:text-sm">
                {t('cinema_nextEpisodePrompt', { number: nextEpisodeTarget.episodeNum })}
              </p>
            </div>

            <div className="mt-5 flex gap-2 md:mt-6 md:gap-3">
              <button
                type="button"
                onClick={() => setNextEpisodeTarget(null)}
                className="flex-1 rounded-xl bg-background-800 py-2.5 text-sm font-medium text-foreground-400 transition-colors hover:bg-background-700 md:py-3"
              >
                {t('cinema_cancel')}
              </button>
              <button
                type="button"
                onClick={() => handleEpisodeClick(nextEpisodeTarget)}
                className="flex-1 rounded-xl bg-accent-500 py-2.5 text-sm font-semibold text-background-950 transition-colors hover:bg-accent-500/90 md:py-3"
              >
                {t('cinema_playNextEpisode')}
              </button>
            </div>
          </div>
        </div>
      )}

      {unlockTarget && (
        <div className="cinema-modal-safe fixed inset-0 z-[60] flex items-center justify-center px-4 pt-4 sm:p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => { setUnlockTarget(null); setShowInsufficient(false); }} />
          <div className="relative w-full max-w-[360px] rounded-2xl border border-background-800 bg-background-900 p-4 shadow-2xl animate-ao-fade-in-up sm:p-5">
            <button
              onClick={() => { setUnlockTarget(null); setShowInsufficient(false); }}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-background-800 text-foreground-500 transition-colors hover:text-foreground-300 cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>

            <div className="text-center">
              <span className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-accent-500/15 text-accent-500 sm:h-12 sm:w-12">
                <i className="ri-film-line text-xl"></i>
              </span>
              <h3 className="font-heading text-base font-bold text-foreground-50">{t('cinema_unlock')}</h3>
              <p className="mt-1 text-xs text-foreground-500 sm:text-sm">
                「{series.title}」第{unlockTarget.episodeNum}集 · {unlockTarget.title}
              </p>
              <p className="mt-2 text-xs text-foreground-400">
                {t('cinema_unlockDesc', { cost: unlockTarget.cost })}
              </p>

              <div className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-background-800/60 px-4 py-2">
                <span className="w-4 h-4 flex items-center justify-center text-accent-500 flex-shrink-0">
                  <i className="ri-coin-line"></i>
                </span>
                <span className="text-xs text-foreground-400">{t('cinema_balance', { coins: member.pointsBalance })}</span>
              </div>

              {showInsufficient && (
                <p className="text-xs text-red-400 mt-2 md:mt-3">{t('cinema_insufficient')}</p>
              )}
              {unlockError && <p className="text-xs text-red-400 mt-2 md:mt-3">{unlockError}</p>}
            </div>

            <div className="mt-4 flex gap-2.5">
              <button
                onClick={() => { setUnlockTarget(null); setShowInsufficient(false); }}
                className="flex-1 rounded-xl bg-background-800 py-2.5 text-sm font-medium text-foreground-400 transition-colors hover:bg-background-700 cursor-pointer whitespace-nowrap"
              >
                {t('cinema_cancel')}
              </button>
              <button
                onClick={handleUnlockConfirm}
                className="flex-1 rounded-xl bg-accent-500 py-2.5 text-sm font-semibold text-background-950 transition-colors hover:bg-accent-500/90 cursor-pointer whitespace-nowrap"
              >
                {t('cinema_unlockConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      <CinemaRechargeModal
        open={rechargeOpen}
        onClose={() => setRechargeOpen(false)}
        loadOptions={async () => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return fetchMemberPaymentOptions(token);
        }}
        createTicket={async (packageId, payMethod) => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return createMemberPaymentTicket(packageId, payMethod, token);
        }}
        createOrder={async (packageId, payMethod) => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return createMemberRechargeOrder(packageId, payMethod, token);
        }}
        queryOrder={async (outTradeNo) => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return queryMemberRechargeOrder(outTradeNo, token);
        }}
        onRechargeSuccess={() => {
          void refreshCurrentMember().then(setMember);
        }}
      />
    </div>
  );
}
